import { Injectable } from '@angular/core';
import { cloneDeep, isEqual } from 'lodash';
import { Action } from 'redux';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ChangeSourceType, DocumentChangedInfo, StateChangeInfo, FormComponent } from '@formbird/types';
import { formDocumentChanged, formResetDocumentChanged } from '../../redux/actions';
import { select } from '../../redux/decorators/select';
import { IApplicationState } from '../../redux/state/application.state';
import { AppStore } from '../../redux/store/app.store';
import { ValidationService } from '../validation/validation.service';

@Injectable({
  providedIn: 'root'
})
export class ChangedDocumentService {

  private static CHANGED_DOCUMENT_PATTERN_LENGTH = 2;
  private static CHANGED_FIELD_PATTERN_LENGTH = 3;

  @select(['formState', 'documents']) documents$: Observable<any>;

  @select(['formState', 'recordedStateChanges']) recordedStateChanges$: Observable<any>;

  constructor(
    private appStore: AppStore<IApplicationState>,
    private validationService: ValidationService
  ) {
  }

  dispatchAction(action: Action) {
    this.appStore.dispatch(action);
  }

  /**
   * Provide the change information for the specified document field that the component binds to
   * @param component the component binds to the field
   * @param newValue the changed/new value
   * @param controlName the registered control manages the field value
   * @param options the options
   */
  valueChanged(component: FormComponent, newValue: any, controlName?: string, options?: any) {

    const documentChangedInfo: DocumentChangedInfo = {
      documentId: component.document.documentId,
      fieldName: component.fieldName,
      newValue: newValue,
      formParameters: component.formParameters,
      // default source value change is made from the component
      sourceType: options?.sourceType ? options?.sourceType : ChangeSourceType.COMPONENT,
      controlName: controlName
    };

    this.appStore.dispatch(formDocumentChanged(documentChangedInfo));
  }

  executeOnFieldChangeWithNoChanges(component: FormComponent) {
    const documentChangedInfo: DocumentChangedInfo = {
      documentId: component.document.documentId,
      fieldName: component.fieldName,
      newValue: null,
      formParameters: component.formParameters,
      // default source value change is made from the component
      sourceType: ChangeSourceType.COMPONENT,
      shouldCompareValues: false
    };
    this.validationService.executeOnFieldChangeWithNoChanges(documentChangedInfo);
  }

  resetChangeDocument() {
    this.appStore.dispatch(formResetDocumentChanged());
  }

  /**
   * Recorded state changes path format come from the rfc6902 pattern but the array instead of string.
   * The path for each the state documents change should be:
   *     0: "documents"
   *     1: <documentId> ==> CHANGED_DOCUMENT_PATTERN_LENGTH
   *     2: <changedFieldName> ===> CHANGED_FIELD_PATTERN_LENGTH
   */
  private filterChangedField(documentId, fieldName, changedItem, inversePatches) {

    // path pattern is: ['documents', '<documentId>', '<fieldName>']
    if (changedItem.path.length >= ChangedDocumentService.CHANGED_FIELD_PATTERN_LENGTH) {
      return changedItem.path[0] === 'documents' && changedItem.path[1] === documentId && changedItem.path[2] === fieldName;
    }

    // path pattern is: ['documents', '<documentId>']
    if (changedItem.path.length === ChangedDocumentService.CHANGED_DOCUMENT_PATTERN_LENGTH) {
      const changedDocument = changedItem.value;
      const inversedDocuments = inversePatches.filter(item => {
        return item.path.length === ChangedDocumentService.CHANGED_DOCUMENT_PATTERN_LENGTH &&
          item.path[0] === 'documents' &&
          item.path[1] === documentId;
      });
      if (inversedDocuments && inversedDocuments.length) {
        return !isEqual(changedDocument[fieldName], inversedDocuments[0].value[fieldName]);
      }
    }

    return false;

  }

  private filterSelectedField(stateChanges: StateChangeInfo, fieldName, documentId) {

    if (stateChanges.patches) {
      return stateChanges.patches.filter(item => {
        return this.filterChangedField(documentId, fieldName, item, stateChanges.inversePatches);
      });
    }

    return false;
  }

  private filterChangedDocument(documentId, changedItem, inversePatches) {
    // return changedItem.path.length >= ChangedDocumentService.CHANGED_DOCUMENT_PATTERN_LENGTH &&
    //   changedItem.path[0] === 'documents' &&
    //   changedItem.path[1] === documentId;

    // path pattern is: ['documents', '<documentId>', '<fieldName>']
    if (changedItem.path.length >= ChangedDocumentService.CHANGED_FIELD_PATTERN_LENGTH) {
      return changedItem.path[0] === 'documents' && changedItem.path[1] === documentId;
    }

    // path pattern is: ['documents', '<documentId>']
    if (changedItem.path.length === ChangedDocumentService.CHANGED_DOCUMENT_PATTERN_LENGTH) {
      const changedDocument = changedItem.value;
      const inversedDocuments = inversePatches.filter(item => {
        return item.path.length === ChangedDocumentService.CHANGED_DOCUMENT_PATTERN_LENGTH &&
          item.path[0] === 'documents' &&
          item.path[1] === documentId;
      });
      if (inversedDocuments && inversedDocuments.length) {
        return !isEqual(changedDocument, inversedDocuments[0].value);
      }
    }

    return false;
  }

  private filterSelectedDocument(stateChanges: StateChangeInfo, documentId) {
    if (stateChanges.patches) {
      return stateChanges.patches.filter(item => {
        return this.filterChangedDocument(documentId, item, stateChanges.inversePatches);
      });
    }

    return false;
  }

  private isChangedFromTheSameSource(stateChanges: StateChangeInfo, fieldName: string, documentId, controlName?: string) {
    const changedSourceInfo = stateChanges.changedSourceInfo;
    return changedSourceInfo
      && changedSourceInfo.type === ChangeSourceType.COMPONENT
      && changedSourceInfo.fieldName === fieldName
      && changedSourceInfo.documentId === documentId
      && changedSourceInfo.controlName === controlName;
  }

  /**
   * Listen value changes on a specified document field
   * @param fieldName the field name of document
   * @param documentId the document id
   * @param controlName the registered control that holds the field value in this component. It is used to check on the source of change
   * to avoid applying changes itself and also applying the value from the components that share the field name.
   * @param options provides more information for the watch.
   */
  watch(fieldName, documentId, controlName?: string, options?: any) {

    return this.recordedStateChanges$.pipe(
      filter((stateChanges: StateChangeInfo) => {
        return (stateChanges.recordedUndoRedo || this.filterSelectedField(stateChanges, fieldName, documentId).length)
          && (options?.forceWatch || !this.isChangedFromTheSameSource(stateChanges, fieldName, documentId, controlName));
      }),
      map((stateChanges: StateChangeInfo) => {
        return this.appStore.getState().formState.documents[documentId][fieldName];
      })
    );

  }

  watchDocument(documentId) {
    return this.recordedStateChanges$.pipe(
      filter((stateChanges: StateChangeInfo) => {
        return stateChanges.recordedUndoRedo || this.filterSelectedDocument(stateChanges, documentId).length;
      }),
      map((stateChanges: StateChangeInfo) => {
        return this.appStore.getState().formState.documents[documentId];
      })
    );
  }

  watchStoreChanges() {
    return this.recordedStateChanges$;
  }
}
